<template>
    <v-dialog v-model="dialog" fullscreen persistent>
        <v-card style="display: flex; flex-direction: column; height: 100%;">
            <v-card-title style="background-color: #1a5b0f !important; display: flex; justify-content: space-between; align-items: center;">
                <div class="w-75 text-truncate text-white">
                    <span class="font-weight-bold text-h6 text-white">{{ dialog_title }}</span>
                    <div class="text-caption text-white">{{ dialog_sub_title }}</div>
                </div>
                <v-btn v-if="loading == false" flat icon color="darkGreen" @click="closeDialog()">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text style="overflow-y: auto; max-height: calc(100vh - 100px);">
                <v-form ref="form" class="mt-2">
                    <v-row dense>
                        <v-col cols="12" md="6">
                            <v-text-field
                                v-model="title"
                                variant="outlined"
                                label="Title"
                                density="comfortable"
                                solo
                                :rules="[formRules.required]"
                                rounded="lg"
                                color="primary"
                            ></v-text-field>
                        </v-col>
                        <v-col cols="12" md="6" class="text-right">
                            <div class="font-weight-bold">
                                {{ subTitle }}
                            </div>
                        </v-col>
                        <v-col cols="12">
                            <v-card class="dt-container">
                                <v-data-table
                                    v-model="selectedReports"
                                    show-select
                                    return-object
                                    :mobile="isMobileOrTablet()"
                                    :headers="headers"
                                    :items="reportsData"
                                    :search="search"
                                    :loading="loading"
                                    :items-per-page="30"
                                    :items-per-page-options="[30, 40, 50, 100, -1]"
                                    >

                                    <template v-slot:item.concern="{ item }">
                                        <div style="max-width: 300px;">
                                            {{ item.concern }} {{ item.termId ? item.termId == 1 ? '(Mid Term)' : '(Final Term)' : null }}
                                        </div>
                                    </template>

                                    <template v-slot:item.deadline="{ item }">
                                        <div style="max-width: 100px;">
                                            {{ formatDate(item.deadline) }}
                                        </div>
                                    </template>

                                    <template v-slot:item.reports="{ item }">
                                        <div style="max-width: 300px;">
                                            <div
                                            v-for="report in item.reports"
                                            :key="report.officeId"
                                            class="my-2"
                                            style="display: flex; flex-direction: column;"
                                            >
                                            <div style="display: flex; justify-content: space-between;">
                                                <strong>{{ report.officeAbbrev }}</strong>
                                                <span class="text-caption">🟢 Before Deadline: {{ report.beforeDeadlinePercentage }}% ({{ report.beforeDeadlineCount }})</span>
                                            </div>
                                            <div style="display: flex; justify-content: space-between; padding-left: 1.5rem;">
                                                <span style="visibility: hidden;">{{ report.officeAbbrev }}</span>
                                                <span class="text-caption">🔴 After Deadline: {{ report.afterDeadlinePercentage }}% ({{ report.afterDeadlineCount }})</span>
                                            </div>
                                            </div>
                                        </div>
                                    </template>

                                    <template v-slot:item.remarks="{ item }">
                                        <div style="max-width: 300px; white-space: pre-wrap;">
                                            {{ item.remarks || '—' }}
                                        </div>
                                    </template>

                                    <template v-slot:[`item.action`]="{ item }">
                                        <div class="d-flex justify-end ga-2">
                                            <v-btn @click="editDetails(item)" size="small" variant="outlined" text="Edit" color="orange" elevation="3"></v-btn>
                                        </div>
                                    </template>

                                    <template v-slot:loading>
                                        <v-progress-linear indeterminate color="primary" height="5" class="mt-1 rounded-lg"></v-progress-linear>
                                    </template>
                                </v-data-table>
                            </v-card>
                        </v-col>
                    </v-row>
                </v-form>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions class="pa-3" style="margin-top: auto;">
                <v-spacer></v-spacer>
                <v-btn class="mr-1" :disabled="loading" text="Close" variant="outlined" elevation="1" @click="closeDialog()"></v-btn>
                <v-btn class="mr-1" :loading="loading" text="Print Report" variant="elevated" elevation="1" color="darkGreen" @click="generatePDF()"></v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <ConsolidatedReportDetailsDialog ref="ConsolidatedReportDetailsDialog" @refresh="init()" />
</template>

<script>
import axios from 'axios';
import { defineComponent } from 'vue'
import ConsolidatedReportDetailsDialog from './Sub-Dialogs/ConsolidatedReportDetailsDialog.vue';
import headerImage from '@/assets/header-landscape.png';
import footerImage from '@/assets/footer-landscape.png';

export default defineComponent({
    emits: ["refresh"],
    components: {
        ConsolidatedReportDetailsDialog
    },
    data () {
        return {
            dialog: false,
            dialog_title: null,
            dialog_sub_title: null,
            action: null,
            loading: false,
            search: null,

            reportsData: [],
            selectedReports: [],

            title: null,
            subTitle: null,
            officeName: 'Office of the Vice President for Academic Affairs',

            headers: [
                { title: 'Area of Concern', align: 'start', sortable: false, key: 'concern' },
                { title: 'Due Date', align: 'start', sortable: false, key: 'deadline' },
                { title: 'Reports', align: 'start', sortable: false, key: 'reports' },
                { title: 'Remarks', align: 'start', sortable: false, key: 'remarks' },
                { title: 'Actions', align: 'end', sortable: false, key: 'action' },
            ]
        }
    },
    methods: {
        init(){
            this.dialog = true
            this.dialog_title = 'Print Consolidated Report'
            this.dialog_sub_title = 'Please select following compliances that includes to the report'
            this.action = 'print'

            this.loadReports()
            const now = new Date();
            const month = now.toLocaleString('default', { month: 'long' });
            const day = now.getDate();
            const year = now.getFullYear();

            this.title = 'VPAA Monitoring'
            this.subTitle = 'S.Y. ' + this.$store.state.semester_schoolyear.schoolyear + ' as of ' + `${month} ${day}, ${year}`;
        },
        async loadReports(){
            this.loading = true
            const token = localStorage.getItem('aums-token')
            await axios.get(process.env.VUE_APP_SERVER_URL + '/reports/consolidated/' + this.$store.state.semester_schoolyear.id,
            { headers:
                { 
                    'Authorization': `Bearer ${token}`,
                }
            }
            ).then((res)=>{
                if(res.data){
                    this.reportsData = res.data.reports
                    this.selectedReports = [...this.reportsData]
                    this.loading = false
                }
            }).catch((error)=>{
                if(error.response.status == 401){
                    this.loading = false

                    localStorage.removeItem("vuex");
                    localStorage.removeItem("aums-token");
                    window.location.reload();
                }
                else{
                    console.log(error)
                }
            });
        },
        editDetails(data){
            this.$refs.ConsolidatedReportDetailsDialog.initEdit(data)
        },
        formatDate(value) {
            const date = new Date(value);
            if (isNaN(date.getTime())) {
                return value;
            }
            return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            });
        },
        async getBase64Image(url) {
            return new Promise((resolve, reject) => {
                const img = new Image();
                img.crossOrigin = 'Anonymous';
                img.src = url;
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    canvas.width = img.width;
                    canvas.height = img.height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0);
                    resolve(canvas.toDataURL('image/png')); // Convert to Base64
                };
                img.onerror = error => reject(error);
            });
        },
        async generatePDF() {
            if (!this.selectedReports || this.selectedReports.length === 0) {
                alert("No reports selected.");
                return;
            }

            const headerImageBase64 = await this.getBase64Image(headerImage);
            const footerImageBase64 = await this.getBase64Image(footerImage);
            const officeName = this.officeName;

            const allOffices = [
                ...new Set(
                    this.selectedReports.flatMap(r => r.reports.map(rep => rep.officeAbbrev))
                )
            ];

            // Header rows
            const headerRow1 = [
                { rowSpan: 3, text: '', style: 'tableHeader', alignment: 'center' },
                { rowSpan: 3, text: 'Area of Concern', style: 'tableHeader', alignment: 'center', margin: [0, 30, 0, 0] },
                { rowSpan: 3, text: 'Due Date', style: 'tableHeader', alignment: 'center', margin: [0, 30, 0, 0] },
                { colSpan: allOffices.length * 2, text: 'Submissions per Institute/Unit (%)', style: 'tableHeader', alignment: 'center' },
                ...Array(allOffices.length * 2 - 1).fill({}),
                { rowSpan: 3, text: 'Remarks', style: 'tableHeader', alignment: 'center', margin: [0, 30, 0, 0] }
            ];

            const headerRow2 = [
                {}, {}, {},
                ...allOffices.flatMap(off => {
                    const report = this.selectedReports.flatMap(r => r.reports).find(r => r.officeAbbrev === off);
                    const facultyCount = report?.totalFacultyWithLoad ?? 0;

                    return [ // ← this return was missing
                        {
                            colSpan: 2,
                            stack: [
                                { text: off, style: 'tableHeader', alignment: 'center' },
                                { text: `Nos. of Faculty: ${facultyCount}`, fontSize: 8, alignment: 'center', margin: [0, 2, 0, 0] }
                            ],
                            alignment: 'center',
                        },
                        {}
                    ];
                }),
                {}
            ];

            const headerRow3 = [
                {}, {}, {},
                ...allOffices.flatMap(() => [
                    {
                        stack: [
                            { text: 'Before', bold: true, style: 'tableHeader' },
                            { text: 'Deadline', margin: [0, 2, 0, 0] }
                        ],
                        alignment: 'center',
                    },
                    {
                        stack: [
                            { text: 'After', bold: true, style: 'tableHeader' },
                            { text: 'Deadline', margin: [0, 2, 0, 0] }
                        ],
                        alignment: 'center',
                    }
                ]),
                {}
            ];

            // Data rows
                const dataRows = this.selectedReports.map((item, index) => {
                const dueDate = this.formatDate(item.deadline);
                const term = item.termId === 1 ? ' (Mid Term)' : item.termId === 2 ? ' (Final Term)' : '';

                const officeCells = allOffices.flatMap(abbrev => {
                    const officeReport = item.reports.find(r => r.officeAbbrev === abbrev);

                    const before = officeReport?.beforeDeadlinePercentage ?? 0;
                    const after = officeReport?.afterDeadlinePercentage ?? 0;

                    const beforeDisplay = before == 0.00 ? '–' : `${before}%\n(${officeReport.beforeDeadlineCount})`;
                    const afterDisplay = after == 0.00 ? '–' : `${after}%\n(${officeReport.afterDeadlineCount})`;

                    return [
                        { text: beforeDisplay, alignment: 'center', margin: [0, 6, 0, 6] },
                        { text: afterDisplay, alignment: 'center', margin: [0, 6, 0, 6] }
                    ];
                });

                return [
                    { text: (index + 1).toString(), alignment: 'center', margin: [0, 6, 0, 6] },
                    { text: item.concern + term, margin: [6, 6, 6, 6] },
                    { text: dueDate, alignment: 'center', margin: [0, 6, 0, 6] },
                    ...officeCells,
                    {
                        stack: (item.remarks || '—').split('\n').map(line => {
                            const match = line.match(/^\s*([A-Za-z]+)\s*-\s*(.*)$/);
                            if (match) {
                                const [, abbrev, rest] = match;
                                return {
                                    text: [
                                        { text: abbrev + ' - ', bold: true },
                                        { text: rest, italics: true }
                                    ],
                                    margin: [0, 1, 0, 1]
                                };
                            } else {
                                return { text: line, margin: [0, 1, 0, 1] };
                            }
                        }),
                        alignment: 'left',
                        fontSize: 9,
                    }
                ];
            });

            const tableBody = [
                headerRow1,
                headerRow2,
                headerRow3,
                ...dataRows
            ];

            

            const docDefinition = {
                defaultStyle: {
                    font: "ArialNarrow"
                },
                info: {
                    title: this.title
                },
                pageSize: {
                    width: 936,
                    height: 612
                },
                pageOrientation: 'landscape',

                pageMargins: [35, 120, 35, 100],

                header: (currentPage, pageCount, pageSize) => {
                    return {
                        stack: [
                            {
                                image: headerImageBase64,
                                width: pageSize.width - 40,
                                alignment: 'center',
                                margin: [0, 8, 0, 0]
                            },
                            {
                                text: officeName,
                                fontSize: 18,
                                bold: true,
                                alignment: 'center',
                                font: "CenturySchoolBook"
                            }
                        ]
                    };
                },

                footer: (currentPage, pageCount, pageSize) => ({
                    image: footerImageBase64,
                    width: pageSize.width - 40,
                    alignment: 'center',
                    margin: [0, 25, 0, 0]
                }),

                content: [
                    {
                        text: this.title,
                        fontSize: 12,
                        bold: true,
                        alignment: 'center',
                        margin: [0, 5, 0, 0],
                    },
                    {
                        text: this.subTitle,
                        fontSize: 11,
                        bold: true,
                        alignment: 'center',
                        margin: [0, 0, 0, 0],
                    },
                    {
                        style: 'table',
                        color: '#444',
                        table: {
                            dontBreakRows: true,
                            widths: [
                                30, 110, 70,
                                ...allOffices.flatMap(() => ['*', "*"]),
                                130
                            ],
                            headerRows: 3,
                            body: tableBody
                        },
                        layout: {
                            hLineWidth: () => 0.5,
                            vLineWidth: () => 0.5,
                        }
                    },
                    {
                        margin: [0, 40, 0, 0],
                        style: 'tableHeader',
                        table: {
                            dontBreakRows: true,
                            widths: ['40%', '60%'],
                            body: [
                            [
                                {
                                stack: [
                                    { text: 'Prepared by:', bold: true, margin: [0, 0, 0, 10] },
                                    { text: 'GLORY JANE S. DUENA', decoration: 'underline', margin: [0, 15, 0, 0] },
                                    { text: 'VPAA Staff', bold: false }
                                ],
                                border: [false, false, false, false]
                                },
                                {
                                stack: [
                                    { text: 'Attested by:', bold: true, alignment: 'left', margin: [0, 0, 0, 10] },
                                    {
                                    columns: [
                                        {
                                        width: '*',
                                        stack: [
                                            {
                                            text: 'GIRLEY S. GUMANAO',
                                            bold: true,
                                            decoration: 'underline',
                                            alignment: 'left'
                                            },
                                            { text: 'VP for Academic Affairs', alignment: 'left', bold: false }
                                        ]
                                        },
                                        {
                                        width: '*',
                                        stack: [
                                            {
                                            text: 'ROSIE LYNN P. TEJADA',
                                            bold: true,
                                            decoration: 'underline',
                                            alignment: 'left'
                                            },
                                            { text: 'Dean, IAAS', alignment: 'left', bold: false }
                                        ]
                                        },
                                        {
                                        width: '*',
                                        stack: [
                                            {
                                            text: 'ARNOLD M. DUPING',
                                            bold: true,
                                            decoration: 'underline',
                                            alignment: 'left'
                                            },
                                            { text: 'Dean, ITEd', alignment: 'left', bold: false }
                                        ]
                                        }
                                    ],
                                    margin: [0, 15, 0, 0]
                                    },
                                    {
                                    columns: [
                                        {
                                        width: '*',
                                        stack: [
                                            {
                                            text: 'MARK VAN M. BULADACO',
                                            bold: true,
                                            decoration: 'underline',
                                            alignment: 'left'
                                            },
                                            { text: 'Dean, IC', alignment: 'left', bold: false }
                                        ]
                                        },
                                        {
                                        width: '*',
                                        stack: [
                                            {
                                            text: 'FLORIE ANN L. FERMIL',
                                            bold: true,
                                            decoration: 'underline',
                                            alignment: 'left'
                                            },
                                            { text: 'Dean, ILEGG', alignment: 'left', bold: false }
                                        ]
                                        },
                                        {
                                        width: '*',
                                        stack: [
                                            {
                                            text: 'NICKEL JEAN L. SASTINE',
                                            bold: true,
                                            decoration: 'underline',
                                            alignment: 'left'
                                            },
                                            { text: 'Dean, IAdS', alignment: 'left', bold: false }
                                        ]
                                        }
                                    ],
                                    margin: [0, 15, 0, 0]
                                    }
                                ],
                                border: [false, false, false, false]
                                }
                            ]
                            ]
                        },
                        layout: 'noBorders'
                    }
                ],

                styles: {
                    tableHeader: {
                        fontSize: 10,
                        bold: true
                    },
                    table: {
                        fontSize: 10,
                        margin: [0, 5, 0, 0]
                    },
                },
            };

            pdfMake.createPdf(docDefinition).open();
        },
        closeDialog(){
            this.$refs.form.reset()
            this.dialog = false
        }
    },
})
</script>

<style scoped>

</style>